import { useEffect, useRef, useState } from 'react';
import { layoutSizes } from '@/theme/layoutSizes';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { PageBreadcrumbs } from '@/ui/layout/PageBreadcrumbs';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import { MenuIcon, NotificationsIcon, PersonIcon } from '@/ui/primitives';
import { s } from '../../theme/tokens';
import {
  Box,
  Drawer,
  IconButton,
  ListSubheader,
  MenuItem,
  MenuList,
  Text,
  useTheme,
} from '../primitives';
import { Popover } from '../primitives/Popover';
import { Stack } from './Stack';
import type { PropsWithChildren, ReactElement, ReactNode } from 'react';

type AppShellProps = PropsWithChildren<{
  title?: ReactNode;
  nav?: ReactNode;
  actions?: ReactNode;
  sidebar?: ReactNode;
  sidebarCollapsed?: boolean;
  showDefaultActions?: boolean;
}>;

export const AppShell = ({
  title,
  nav,
  actions,
  sidebar,
  sidebarCollapsed = false,
  showDefaultActions = true,
  children,
}: AppShellProps): ReactElement => {
  const theme = useTheme();
  const layoutMode = useLayoutMode();
  const { items: breadcrumbItems, visible: breadcrumbsVisible } = useBreadcrumbs();
  const showBreadcrumbs = breadcrumbsVisible && breadcrumbItems.length > 0;
  const isMobile = layoutMode !== layoutModeTokens.wide;
  const headerPaddingX = isMobile ? s('sm') : s('xl');
  const [open, setOpen] = useState(false);
  const [notificationsAnchor, setNotificationsAnchor] = useState<HTMLElement | null>(null);
  const [userAnchor, setUserAnchor] = useState<HTMLElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const hasSidebar = Boolean(sidebar);
  const desktopSidebarWidth = sidebarCollapsed
    ? layoutSizes.sidebar.collapsedWidth
    : theme.app.sizes.sidebarWidth;
  const hasHeader = [title, nav, actions, hasSidebar].some(Boolean);
  const useGridHeaderLayout = isMobile && hasSidebar;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const offset = hasSidebar && !isMobile ? `${desktopSidebarWidth}px` : '0px';
    document.body.style.setProperty('--app-dialog-offset', offset);
    return () => {
      document.body.style.removeProperty('--app-dialog-offset');
    };
  }, [desktopSidebarWidth, hasSidebar, isMobile]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const headerElement = headerRef.current;
    if (!headerElement) {
      document.body.style.setProperty('--app-topbar-height', '0px');
      return () => {
        document.body.style.removeProperty('--app-topbar-height');
      };
    }

    const updateTopbarHeight = (): void => {
      const nextHeight = Math.round(headerElement.getBoundingClientRect().height);
      document.body.style.setProperty('--app-topbar-height', `${nextHeight}px`);
    };

    document.body.style.setProperty('--app-topbar-height', `${layoutSizes.topBar.height}px`);

    const frameId = window.requestAnimationFrame(updateTopbarHeight);
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            updateTopbarHeight();
          });
    resizeObserver?.observe(headerElement);

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      document.body.style.removeProperty('--app-topbar-height');
    };
  }, [hasHeader, headerPaddingX]);
  return (
    <Box
      sx={(currentTheme) => ({
        '--app-shell-corner-radius': '8px',
        height: '100dvh',
        minHeight: '100vh',
        backgroundColor: `${resolveSemanticColor(currentTheme, 'pageBg')} !important`,
        backgroundImage: 'none',
        opacity: 1,
        isolation: 'isolate',
        display: 'grid',
        transition: 'grid-template-columns 180ms ease',
        gridTemplateColumns:
          hasSidebar && !isMobile ? `${desktopSidebarWidth}px minmax(0, 1fr)` : '1fr',
        gridTemplateRows: hasHeader ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)',
        overflow: 'hidden',
      })}
    >
      <a className="flow-skip-link" href="#main-content">
        Skip to main content
      </a>
      {hasHeader && (
        <Box
          component="header"
          ref={headerRef}
          sx={(currentTheme) => ({
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: `${resolveSemanticColor(currentTheme, 'surfacePaper')} !important`,
            backgroundImage: 'none',
            opacity: 1,
            isolation: 'isolate',
            borderBottom: `1px solid ${currentTheme.app.colors.border}`,
            minHeight: layoutSizes.topBar.height,
            gridColumn: hasSidebar && !isMobile ? '2' : '1',
            gridRow: '1',
            paddingTop: s('xs'),
            paddingBottom: s('xs'),
            minWidth: 0,
          })}
        >
          <Box
            sx={{
              display: useGridHeaderLayout ? 'grid' : 'flex',
              gridTemplateColumns: useGridHeaderLayout
                ? 'minmax(0, 1fr) auto minmax(0, 1fr)'
                : undefined,
              alignItems: 'center',
              gap: s('md'),
              flexWrap: 'nowrap',
              minHeight: layoutSizes.topBar.height,
              minWidth: 0,
              width: '100%',
              px: headerPaddingX,
            }}
          >
            {hasSidebar && isMobile ? (
              <IconButton
                aria-label="Open navigation"
                onClick={() => {
                  setOpen(true);
                }}
                sx={{
                  '&:hover': { backgroundColor: 'transparent' },
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: s('sm'),
                  marginTop: (muiTheme) => muiTheme.spacing(0),
                  padding: (muiTheme) => muiTheme.spacing(0.25),
                  width: (muiTheme) => muiTheme.app.iconSizes.xxl,
                  height: (muiTheme) => muiTheme.app.iconSizes.xxl,
                  lineHeight: 0,
                  '& .MuiBox-root': {
                    width: (muiTheme) => muiTheme.app.iconSizes.xxl,
                    height: (muiTheme) => muiTheme.app.iconSizes.xxl,
                  },
                  '& .MuiBox-root svg': {
                    width: '100%',
                    height: '100%',
                  },
                  '& svg': {
                    width: (muiTheme) => muiTheme.app.iconSizes.xxl,
                    height: (muiTheme) => muiTheme.app.iconSizes.xxl,
                    display: 'block',
                  },
                }}
              >
                <MenuIcon fontSize="inherit" />
              </IconButton>
            ) : null}
            {title ? (
              <Box
                sx={{
                  justifySelf: useGridHeaderLayout ? 'center' : 'start',
                  textAlign: useGridHeaderLayout ? 'center' : 'left',
                }}
              >
                <Text role="sectionTitle">{title}</Text>
              </Box>
            ) : null}
            {nav && !useGridHeaderLayout ? (
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>{nav}</Box>
            ) : !useGridHeaderLayout ? (
              <Box sx={{ flexGrow: 1, minWidth: 0 }} />
            ) : null}
            <Stack
              direction="row"
              gap="sm"
              sx={{
                justifySelf: useGridHeaderLayout ? 'stretch' : 'end',
                justifyContent: useGridHeaderLayout ? 'flex-end' : undefined,
                minWidth: 0,
              }}
            >
              {showDefaultActions ? (
                <>
                  <IconButton
                    size="large"
                    aria-label="Notifications"
                    onClick={(event) => {
                      setNotificationsAnchor(event.currentTarget);
                    }}
                    sx={{
                      '&:hover': { backgroundColor: 'transparent' },
                      '& svg': {
                        width: (muiTheme) => muiTheme.app.iconSizes.lg,
                        height: (muiTheme) => muiTheme.app.iconSizes.lg,
                      },
                    }}
                  >
                    <NotificationsIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="large"
                    aria-label="Account"
                    onClick={(event) => {
                      setUserAnchor(event.currentTarget);
                    }}
                    sx={{
                      '&:hover': { backgroundColor: 'transparent' },
                      '& svg': {
                        width: (muiTheme) => muiTheme.app.iconSizes.lg,
                        height: (muiTheme) => muiTheme.app.iconSizes.lg,
                      },
                    }}
                  >
                    <PersonIcon fontSize="inherit" />
                  </IconButton>
                </>
              ) : null}
              {actions ? <Box>{actions}</Box> : null}
            </Stack>
          </Box>
        </Box>
      )}
      {hasSidebar ? (
        <Box
          component="aside"
          sx={(currentTheme) => ({
            display: isMobile ? 'none' : 'block',
            gridColumn: '1',
            gridRow: hasHeader ? '1 / span 2' : '1',
            width: desktopSidebarWidth,
            transition: 'width 180ms ease',
            backgroundColor: `${resolveSemanticColor(currentTheme, 'surfacePaper')} !important`,
            backgroundImage: 'none',
            opacity: 1,
            isolation: 'isolate',
            borderRight: `1px solid ${currentTheme.app.colors.border}`,
            paddingX: s('none'),
            paddingY: s('none'),
            alignSelf: 'stretch',
            height: '100%',
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
          })}
        >
          {sidebar}
        </Box>
      ) : null}
      <Box
        sx={(currentTheme) => ({
          display: 'grid',
          gridColumn: hasSidebar && !isMobile ? '2' : '1',
          gridRow: hasHeader ? '2' : '1',
          gridTemplateColumns: '1fr',
          minHeight: 0,
          backgroundColor: `${resolveSemanticColor(currentTheme, 'pageBg')} !important`,
          backgroundImage: 'none',
          opacity: 1,
          isolation: 'isolate',
          overflow: 'hidden',
        })}
      >
        {hasSidebar ? (
          <>
            <Drawer
              open={open}
              onClose={() => {
                setOpen(false);
              }}
              sx={{ display: isMobile ? 'block' : 'none' }}
              slotProps={{
                paper: {
                  sx: {
                    width: theme.app.sizes.sidebarWidth,
                    paddingX: s('none'),
                    paddingY: s('none'),
                    backgroundColor: `${resolveSemanticColor(theme, 'surfacePaper')} !important`,
                    borderRight: `1px solid ${theme.app.colors.border}`,
                    backgroundImage: 'none',
                    opacity: 1,
                    isolation: 'isolate',
                    height: '100%',
                  },
                },
              }}
            >
              <Box
                sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}
                onClickCapture={(event) => {
                  const target = event.target as HTMLElement | null;
                  if (!target) return;
                  if (target.closest('a[data-has-popover="true"]')) return;
                  if (target.closest('a[href]')) {
                    window.setTimeout(() => {
                      setOpen(false);
                    }, 0);
                  }
                }}
              >
                {sidebar}
              </Box>
            </Drawer>
          </>
        ) : null}
        <Box
          component="main"
          id="main-content"
          tabIndex={0}
          sx={(currentTheme) => ({
            flex: 1,
            minWidth: 0,
            px: isMobile && hasSidebar ? s('sm') : s('none'),
            minHeight: 0,
            overflowY: 'auto',
            scrollbarGutter: isMobile ? 'auto' : 'stable both-edges',
            overscrollBehavior: 'auto',
            backgroundColor: `${resolveSemanticColor(currentTheme, 'pageBg')} !important`,
            backgroundImage: 'none',
            opacity: 1,
            isolation: 'isolate',
            scrollbarColor: `${currentTheme.app.colors.border} ${resolveSemanticColor(
              currentTheme,
              'pageBg',
            )}`,
            '&::-webkit-scrollbar': {
              width: '12px',
              height: '12px',
              backgroundColor: resolveSemanticColor(currentTheme, 'pageBg'),
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: resolveSemanticColor(currentTheme, 'pageBg'),
            },
            '&::-webkit-scrollbar-corner': {
              backgroundColor: resolveSemanticColor(currentTheme, 'pageBg'),
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: currentTheme.app.colors.border,
              borderRadius: '999px',
              border: '2px solid transparent',
              backgroundClip: 'content-box',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: currentTheme.palette.text.secondary,
            },
            borderTopLeftRadius: undefined,
            borderTop: 'none',
            borderLeft: 'none',
            boxSizing: 'border-box',
            overflowX: 'auto',
          })}
        >
          {showBreadcrumbs ? (
            <Box
              sx={(currentTheme) => ({
                borderBottom: `1px solid ${currentTheme.app.colors.border}`,
                paddingX: s('md'),
                paddingTop: s('md'),
                paddingBottom: s('md'),
              })}
            >
              <PageBreadcrumbs />
            </Box>
          ) : null}
          {children}
        </Box>
      </Box>
      {showDefaultActions ? (
        <>
          <Popover
            open={Boolean(notificationsAnchor)}
            anchorEl={notificationsAnchor}
            onClose={() => {
              setNotificationsAnchor(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            minWidth={240}
          >
            <MenuList
              dense
              disablePadding
              subheader={
                <ListSubheader
                  disableSticky
                  sx={{
                    typography: 'label',
                    paddingX: s('sm'),
                    paddingY: s('xs'),
                    lineHeight: 1.4,
                    backgroundColor: 'transparent',
                  }}
                >
                  Notifications
                </ListSubheader>
              }
            >
              <MenuItem
                sx={{
                  minHeight: s('2xl'),
                  paddingY: s('xs'),
                  typography: 'body',
                  fontSize: (muiTheme) => muiTheme.typography.caption.fontSize,
                  lineHeight: (muiTheme) => muiTheme.typography.caption.lineHeight,
                  fontWeight: (muiTheme) => muiTheme.typography.caption.fontWeight,
                  color: 'text.secondary',
                }}
              >
                No new alerts.
              </MenuItem>
            </MenuList>
          </Popover>
          <Popover
            open={Boolean(userAnchor)}
            anchorEl={userAnchor}
            onClose={() => {
              setUserAnchor(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            minWidth={200}
          >
            <MenuList
              dense
              disablePadding
              subheader={
                <ListSubheader
                  disableSticky
                  sx={{
                    typography: 'label',
                    paddingX: s('sm'),
                    paddingY: s('xs'),
                    lineHeight: 1.4,
                    backgroundColor: 'transparent',
                  }}
                >
                  Account
                </ListSubheader>
              }
            >
              <MenuItem
                sx={{
                  minHeight: s('2xl'),
                  paddingY: s('xs'),
                  typography: 'body',
                  fontSize: (muiTheme) => muiTheme.typography.caption.fontSize,
                  lineHeight: (muiTheme) => muiTheme.typography.caption.lineHeight,
                  fontWeight: (muiTheme) => muiTheme.typography.caption.fontWeight,
                  color: 'text.secondary',
                }}
              >
                Signed in as demo@flowqa.dev
              </MenuItem>
            </MenuList>
          </Popover>
        </>
      ) : null}
    </Box>
  );
};
